// @ts-strict-ignore
import { ElementRef, Inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { StageBackground, StageBackgroundState, StageView } from 'src/app/api/owcc/models';
import { MQS_MOBILE } from 'src/app/core/config/mq.config';
import { fromCarl, fromMq } from 'src/app/store/common/common.selectors';
import { RATIO_16_9, RATIO_21_9 } from 'src/app/utils/number-constants';
import { WINDOW } from 'src/app/utils/window.token';

import {
  ButtonTooltipTexts,
  DEFAULT_BUTTON_TOOLTIP_TEXTS,
  StageViewStateValue
} from '../awesome-stage/awesome-stage.model';

import { Stickiness } from './main-stage.component';

export interface MainStageState {
  stageView: StageView;
  stageViewState: StageViewStateValue;
  stageBackgrounds: StageBackground[];
  stickiness: Stickiness | undefined;
  hasSelectionBoxLeftNextToIt: boolean;
  stageBackgroundHeight: number; // stage background height is stage height + margin above stage
  isInFullscreenMode: boolean;
  buttonTooltipTexts: ButtonTooltipTexts | undefined;
}

const defaultMainStageState: MainStageState = {
  stageView: StageView.Exterior360,
  stageViewState: StageViewStateValue.Day,
  stageBackgrounds: [],
  stickiness: undefined,
  hasSelectionBoxLeftNextToIt: false,
  stageBackgroundHeight: 0,
  isInFullscreenMode: false,
  buttonTooltipTexts: undefined
};

const MQS_WITH_16X9_RATIO = new Set(MQS_MOBILE);

const DEFAULT_ID = 'MUSEUM';

@Injectable()
export class MainStageStore extends ComponentStore<MainStageState> {
  public readonly stageBackgroundHeight$ = this.select(state => state.stageBackgroundHeight);

  public readonly buttonTooltipTexts$ = this.select(state => state.buttonTooltipTexts);

  public readonly stageView$ = this.select(state => state.stageView);

  public readonly stageViewState$ = this.select(state => state.stageViewState);

  public readonly stageBackgrounds$ = this.select(state => state.stageBackgrounds);

  public readonly hasSelectionBoxLeftNextToIt$ = this.select(state => state.hasSelectionBoxLeftNextToIt);

  public readonly stickinessStyle$ = this.select(state =>
    state.stickiness?.isSticky
      ? {
          position: 'sticky',
          top: `${state.stickiness?.topPosition}px`,
          height: `${state.stickiness?.height}px`
        }
      : {}
  );

  public readonly isStageViewInterior$ = this.select(
    this.stageView$,
    stageView => stageView === StageView.Interior,
    { debounce: true } // 👈 to prevent ExpressionChangedAfterItHasBeenCheckedError
  );

  public readonly isInFullsrceenMode$ = this.select(state => state.isInFullscreenMode);

  public readonly mediaQuery$ = this.store.select(fromMq.selectMediaQuery);

  public readonly stageRatio$ = this.select(
    this.hasSelectionBoxLeftNextToIt$,
    this.mediaQuery$,
    this.isInFullsrceenMode$,
    (hasSelectionBoxLeftNextToIt, mediaQuery, isInFullscreenMode) => {
      if (isInFullscreenMode) {
        return RATIO_21_9;
      }

      if (hasSelectionBoxLeftNextToIt || MQS_WITH_16X9_RATIO.has(mediaQuery)) {
        return RATIO_16_9;
      }

      return RATIO_21_9;
    }
  );

  // for now we take the last entry in the array of stage backgrounds
  public readonly stageBackgroundId$ = this.select(
    this.stageBackgrounds$,
    stageBackgrounds => [...stageBackgrounds]?.pop()?.id || DEFAULT_ID
  );

  public readonly stageBackgroundUrl$ = this.select(
    this.stageBackgroundId$,
    this.stageBackgrounds$,
    this.stageViewState$,
    (id, stageBackgrounds, stageViewState) => {
      const state = Object.values(stageBackgrounds.find(bg => bg.id === id)?.states || []).find(
        (stageBackgroundState: StageBackgroundState) =>
          (stageBackgroundState.stageViewState as StageViewStateValue) === stageViewState
      );

      return state?.backgroundUrl || undefined;
    },
    { debounce: true } // 👈 to prevent ExpressionChangedAfterItHasBeenCheckedError
  );

  public readonly updateStageBackgroundHeight = this.effect(
    (trigger$: Observable<{ stageHeight: number; element: ElementRef<Element> }>) => {
      const updateStageBackgroundHeight = (stageBackgroundHeight: number): void => {
        this.patchState({ stageBackgroundHeight });
      };
      const getPaddingTopAboveStage = (element: ElementRef<Element>): number =>
        Number.parseInt(this.window.getComputedStyle(element.nativeElement)?.paddingTop, 10);

      return trigger$.pipe(
        map(({ stageHeight, element }) => stageHeight + getPaddingTopAboveStage(element)),
        tap((stageBackgroundHeight: number) => {
          updateStageBackgroundHeight(stageBackgroundHeight);
        })
      );
    }
  );

  private readonly getTooltipTexts = this.effect(trigger$ =>
    trigger$.pipe(
      switchMap(() => {
        const prefix = `mainStage.buttonTooltip.`;

        /**
         * @returns Translation or undefined in order to fallback to default english text
         */
        const translate = (key: string): Observable<string | undefined> =>
          // TODO: Fix with MBCC-2538
          // eslint-disable-next-line @ngrx/avoid-mapping-selectors
          this.store.select(fromCarl.selectTranslation(`${prefix}${key}`)).pipe(map(t => t || undefined));

        return combineLatest([
          translate(`turnLightingOn`),
          translate(`turnLightingOff`),
          translate(`openRoof`),
          translate(`closeRoof`),
          translate(`enterFullscreen`),
          translate(`leaveFullscreen`),
          translate(`turnThreeSixtyOn`),
          translate(`turnThreeSixtyOff`),
          translate(`toggleExterior`),
          translate(`toggleInterior`),
          translate(`slideToTrunk`),
          translate(`exitScreenMirroring`),
          translate(`startScreenMirroring`)
        ]);
      }),
      map(
        // eslint-disable-next-line complexity
        ([
          turnLightingOn,
          turnLightingOff,
          openRoof,
          closeRoof,
          enterFullscreen,
          leaveFullscreen,
          turnThreeSixtyOn,
          turnThreeSixtyOff,
          toggleExterior,
          toggleInterior,
          slideToTrunk,
          exitScreenMirroring,
          startScreenMirroring
        ]) => ({
          turnLightingOn: turnLightingOn || DEFAULT_BUTTON_TOOLTIP_TEXTS.turnLightingOn,
          turnLightingOff: turnLightingOff || DEFAULT_BUTTON_TOOLTIP_TEXTS.turnLightingOff,
          openRoof: openRoof || DEFAULT_BUTTON_TOOLTIP_TEXTS.openRoof,
          closeRoof: closeRoof || DEFAULT_BUTTON_TOOLTIP_TEXTS.closeRoof,
          enterFullscreen: enterFullscreen || DEFAULT_BUTTON_TOOLTIP_TEXTS.enterFullscreen,
          leaveFullscreen: leaveFullscreen || DEFAULT_BUTTON_TOOLTIP_TEXTS.leaveFullscreen,
          turnThreeSixtyOn: turnThreeSixtyOn || DEFAULT_BUTTON_TOOLTIP_TEXTS.turnThreeSixtyOn,
          turnThreeSixtyOff: turnThreeSixtyOff || DEFAULT_BUTTON_TOOLTIP_TEXTS.turnThreeSixtyOff,
          toggleExterior: toggleExterior || DEFAULT_BUTTON_TOOLTIP_TEXTS.toggleExterior,
          toggleInterior: toggleInterior || DEFAULT_BUTTON_TOOLTIP_TEXTS.toggleInterior,
          slideToTrunk: slideToTrunk || DEFAULT_BUTTON_TOOLTIP_TEXTS.slideToTrunk,
          exitScreenMirroring: exitScreenMirroring || DEFAULT_BUTTON_TOOLTIP_TEXTS.exitScreenMirroring,
          startScreenMirroring: startScreenMirroring || DEFAULT_BUTTON_TOOLTIP_TEXTS.startScreenMirroring
        })
      ),
      tap((buttonTooltipTexts: ButtonTooltipTexts) => {
        this.patchState({ buttonTooltipTexts });
      })
    )
  );

  public constructor(
    private readonly store: Store,
    @Inject(WINDOW) private readonly window: Window
  ) {
    super(defaultMainStageState);

    this.getTooltipTexts();
  }

  public readonly updateStageView = (stageView: StageView): void => {
    this.patchState({ stageView });
  };

  public readonly updateStageViewState = (stageViewState: StageViewStateValue): void => {
    this.patchState({ stageViewState });
  };

  public readonly updateStageBackgrounds = (stageBackgrounds: StageBackground[] = []): void => {
    this.patchState({ stageBackgrounds });
  };

  public readonly updateStickiness = (stickiness: Stickiness): void => {
    this.patchState({ stickiness });
  };

  public readonly updateFullscreenMode = (isInFullscreenMode: boolean): void => {
    this.patchState({ isInFullscreenMode });
  };

  public readonly updateHasSelectionBoxLeftNextToIt = (hasSelectionBoxLeftNextToIt: boolean): void => {
    this.patchState({ hasSelectionBoxLeftNextToIt });
  };
}
